// Utilities
import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
import WindowMetrics from '../../utilities/window-metrics';
import {SiteSection} from '../../utilities/site-section';

// Components
import NavSection from './nav-section';
import Submenu from './submenu';
import SectionSelector from './section-selector';

const settings = {
    dataAttributes: {
        currentSection: 'data-section',
    },

    classNames: {
        hidden: 'header--hidden',
        hamburger: 'header__hamburger',
        open: 'header--open',
        scroll: 'header--scroll',
        backdrop: 'header__menu-backdrop',
    },

    selectors: {
        conainer: 'header',
        nav: 'nav',
        navMenuContainer: '.header__nav-menu-container',
        navSectionContainers: 'nav [data-section]',
        sectionSelectorContainer: '.header__section-selector',
        forceVisibilityElement: `[data-header-visible="true"]`,
        suppressVisibilityElement: `[data-header-visible="false"]`,
        mobileDisabledElement: `[data-mobile-disabled]`,
    },

    // Settings related to toggling header visibility based on scroll intent
    scrollVelocity: {
        // pixels per millisecond
        triggerSpeed: 1.2,
    },
};

// Our single header instance, created via Header.init()
let header = null;

// Flag for suppressing visibility momentarily, used when scrolling
// upward programatically (e.g., switching tabs)
let suppressVisibilityOverride = false;

export default class Header {
    constructor() {
        this.visible = true;
        this.menuOpen = false;
        this.openSubmenu = null;
        this.selectedSectionId = {
            mobile: null,
            desktop: null,
        };
        this.scrollVelocity = 0;
        this.lastScrollPosition = window.pageYOffset;
        this.lastScrollTime = (new Date()).getTime();

        // Determine the section-specific menu to display
        const initialSectionId = SiteSection.id || '';

        // Locate our DOM elements
        this.container = document.querySelector(settings.selectors.conainer);
        this.nav = this.container.querySelector(settings.selectors.nav);
        this.hamburger = this.container.querySelector(`.${settings.classNames.hamburger}`);
        this.navMenuContainer = this.container
            .querySelector(settings.selectors.navMenuContainer);
        const navSectionContainers = this.container
            .querySelectorAll(settings.selectors.navSectionContainers);
        const sectionSelectorContainer = this.container
            .querySelector(settings.selectors.sectionSelectorContainer);

        if (
            !this.container ||
            !this.nav ||
            !this.hamburger ||
            !this.navMenuContainer ||
            navSectionContainers.length <= 0 ||
            !sectionSelectorContainer
        ) {
            throw new Error('Header not stuctured as expected');
        }

        // Setup nav section, each associated with a section of the site
        this.navSections = [];

        // Setup submenus
        const submenuCallbacks = {
            onOpen: function(submenu) {
                // Close and reset section selector
                closeAndResetSectionSelector.call(this);

                // If a submenu is open, close it
                if (this.openSubmenu) {
                    if (this.openSubmenu != submenu) {
                        this.openSubmenu.close();
                    }
                }

                this.openSubmenu = submenu;
            }.bind(this),

            onClose: function(submenu) {
                if (this.openSubmenu == submenu) {
                    this.openSubmenu = null;
                }
            }.bind(this),
        };

        for (let i=0; i<navSectionContainers.length; i++) {
            const navSection = new NavSection(
                navSectionContainers[i],
                submenuCallbacks,
            );

            this.navSections.push(navSection);
        }

        // Set the section ID on the header as a whole
        this.container.setAttribute('data-section', initialSectionId);
        this.selectMobileSectionId(initialSectionId);
        this.selectDesktopSectionId(initialSectionId);

        // Setup section selector
        this.sectionSelector = new SectionSelector(
            sectionSelectorContainer,
            initialSectionId,
            {
                onOpen: function(submenu) {
                    // If a submenu is open, close it
                    if (this.openSubmenu) {
                        if (this.openSubmenu != submenu) {
                            this.openSubmenu.close();
                        }
                    }

                    this.openSubmenu = submenu;
                }.bind(this),

                onSelectionChange: (sectionId) => {
                    // Update our selected section ID
                    this.selectMobileSectionId(sectionId);
                    this.selectDesktopSectionId(sectionId);
                },
            },
        );

        // Setup <body> touch & click events to close any open menus;
        // click event added specifically for keyboard navigation, which
        // would result in menus getting stuck open if not opened via
        // a "mouseenter" event
        const bodyClickCallback = (event) => {
            // If the section selector is open, close it
            if (this.sectionSelector.isOpen) {
                // Check that this click didn't occur within the open
                // section selector
                if (!event.target.closest(`.${SectionSelector.settings.classNames.submenuOpen}`)) {
                    this.sectionSelector.close();
                }
            }

            // If a submenu is open, close it
            if (this.openSubmenu) {
                // Check that this click didn't occur within the open
                // submenu
                if (!event.target.closest(`.${Submenu.settings.classNames.submenuOpen}`)) {
                    // Close the open submenu
                    this.openSubmenu.close();
                }
            }
        };

        document.body.addEventListener('click', bodyClickCallback);
        document.body.addEventListener('touchstart', bodyClickCallback);

        // Check for any elements that force the header to be
        // visible or hidden while on screen
        const forceVisibilityElements = document
            .querySelectorAll(settings.selectors.forceVisibilityElement);
        const suppressVisibilityElements = document
            .querySelectorAll(settings.selectors.suppressVisibilityElement);

        // Setup hamburger
        this.hamburger.addEventListener('click', toggleMobileMenu.bind(this));
        this.hamburger.addEventListener('touchstart', toggleMobileMenu.bind(this));

        this.hamburger.addEventListener(
            'keydown',
            (event) => {
                if (event.key == 'Enter') {
                    event.preventDefault();
                    event.stopPropagation();

                    toggleMobileMenu.call(this, event);
                }
            },
        );

        // Check for elements that need to be disabled on mobile -
        // this excludes them from keyboard navigation
        const mobileDisabledElements = Array.from(
            this.container
                .querySelectorAll(settings.selectors.mobileDisabledElement),
        );

        const updateMobileDisabledElements = () => {
            if (WindowMetrics.isMobile()) {
                mobileDisabledElements.forEach((element) => {
                    element.disabled = true;
                });
            } else {
                mobileDisabledElements.forEach((element) => {
                    element.disabled = false;
                });
            }
        };

        updateMobileDisabledElements();

        // Setup a mobile-to-desktop breakpoint change event
        WindowMetrics.addBeakpointChangeCallback(
            function(mobileDesktopTransition) {
                if (!mobileDesktopTransition) {
                    return;
                }

                // Close and reset section selector
                closeAndResetSectionSelector.call(this, true);

                // If a submenu is open, close it
                if (this.openSubmenu) {
                    this.openSubmenu.close();
                }

                // Update mobile disabled elements
                updateMobileDisabledElements();

                if (WindowMetrics.isMobile()) {
                    return;
                }

                // We're in desktop
                if (this.menuOpen) {
                    // The menu is open - close it immediately
                    this.closeMenu(true);
                }
            }.bind(this),
        );

        // Setup our scroll event
        const scrollCallback = function() {
            // Do nothing if the mobile menu is open
            if (this.menuOpen) {
                return;
            }

            // If the "suppressVisibilityOverride" flag is set, force the header
            // to stay or become hidden
            if (suppressVisibilityOverride) {
                if (this.visible) {
                    this.setVisibility(false);
                }

                return;
            }

            // First, determine if we should or shouldn't be allowed
            // to become hidden
            const scrollPosition = window.pageYOffset;
            let forceVisibility = false;
            let suppressVisibility = false;
            if (scrollPosition < this.container.clientHeight + 1) {
                // The user hasn't yet scrolled past the height of the
                // header itself - remain visible
                forceVisibility = true;
            } else {
                // If any elements were found on the DOM based off of our
                // force visibility selector, check if any of them are in
                // the viewport with their top edge (roughly) at the top
                // of the viewport - this is basically just for the scroll
                // locked hero components
                for (let i=0; i<forceVisibilityElements.length; i++) {
                    const bounds = forceVisibilityElements[i]
                        .getBoundingClientRect();

                    // The 1 pixel adjustments is to give a little wiggle
                    // room in case the actual pixel locations are fractional
                    if (bounds.top > -1 && bounds.top < 1) {
                        // Element is at the top of the viewport - force
                        // the header to be visible
                        forceVisibility = true;
                    }
                }

                // If any elements were found on the DOM based off of our
                // suppress visibility selector, check if any of them span
                // the top of the viewport - this is basically just for the
                // tab containers that have a sticky button bar
                for (let i=0; i<suppressVisibilityElements.length; i++) {
                    const bounds = suppressVisibilityElements[i]
                        .getBoundingClientRect();

                    if (bounds.top < 0 && bounds.bottom > 0) {
                        // Element spans the top of the viewport - force
                        // the header to be hidden
                        suppressVisibility = true;
                    }
                }
            }

            // If for some reason we've detected that we should both force
            // and suppress visibility simultaneously, let "force" take
            // precedence
            if (forceVisibility) {
                // We're forcing the header to be visible
                if (!this.visible) {
                    // Become visible
                    this.setVisibility(true);
                }

                // Stop here
                return;
            }

            if (suppressVisibility) {
                // We're forcing the header to be hidden
                if (this.visible) {
                    // Become hidden
                    this.setVisibility(false);
                }

                // Stop here
                return;
            }

            // We're sticky - determine if the scroll velocity should cause
            // a change in our visibility
            const scrollTime = (new Date()).getTime();
            const timeDifference = scrollTime - this.lastScrollTime;

            // Filter out large gaps in time and smooth out big changes in velocity
            if (timeDifference > 500) {
                this.scrollVelocity = 0;
            } else {
                this.scrollVelocity =
                    (this.lastScrollPosition - scrollPosition) /
                    timeDifference;
            }

            // Update the previously sampled values to what we just measured
            this.lastScrollPosition = scrollPosition;
            this.lastScrollTime = scrollTime;

            // Check if we've surpassed our scroll speed threshold,
            // scrolling either upward or downward
            if (this.scrollVelocity >= settings.scrollVelocity.triggerSpeed) {
                // We've surpassed the threshold scrolling upward -
                // make the header visible if it isn't
                this.setVisibility(true);
            } else if (
                this.scrollVelocity <= -settings.scrollVelocity.triggerSpeed
            ) {
                // We've surpassed the threshold scrolling downward -
                // make the header unsticky if it isn't
                this.setVisibility(false);
            }
        }.bind(this);

        // Do this after a brief timeout so that the header
        // won't become sticky immediately on page load should
        // the browser decide to change the scroll position
        setTimeout(
            () => {
                window.addEventListener('scroll', scrollCallback);

                // EDIT: not sure I like this because the header is
                // visible for a moment, and also because the hide
                // animation is active at this point and would need
                // to be worked around (this is 1 of 2 places I added
                // code related to this)
                // // If the page has scrolled down to an anchor link,
                // // hide the header initially
                // if (window.location.hash) {
                //     // This is slapdash but it prevents the auto-hide
                //     // from triggering at the top of the page should
                //     // the user scroll up and reload with the hash
                //     // still in the URL
                //     if (window.pageYOffset > 200) {
                //         // This could be more complicated, such as checking the
                //         // scroll position or searching for the item on the page
                //         this.setVisibility(false);
                //     }
                // }
            },
            500,
        );

        // Setup resize event
        window.addEventListener(
            'resize',
            function() {
                // Skip this if we're not open
                if (!this.menuOpen) {
                    return;
                }

                // Adjust whether scrolling in the nav is enabled or not
                adjustScrolling.call(this);
            }.bind(this),
        );

        // EDIT: I'm just going to let people react to the header initially
        // covering content when users arrive via an anchor; another solution
        // might be suggested like scrolling upward after the window has
        // fully loaded (this is 2 of 2 places I added code related to this)
        // // If the page has scrolled down to an anchor link,
        // // hide the header initially
        // if (window.location.hash) {
        //     // This could be more complicated, such as checking the
        //     // scroll position or searching for the item on the page
        //     this.setVisibility(false);
        // }

        // // Testing
        // if (WindowMetrics.isMobile()) {
        //     this.openMenu(true);
        // }
    }

    static init() {
        // Stop if we've already initialized
        if (header) {
            return;
        }

        header = new Header();
    }

    static suppressVisibility(duration = 1000) {
        suppressVisibilityOverride = true;

        setTimeout(
            () => {
                suppressVisibilityOverride = false;
            },
            duration,
        );
    }

    /**
     * @return {Header}
     */
    static get current() {
        return header;
    }

    /**
     * Shows the nav section associated with the specified ID, on mobile
     * @param {string} sectionId
     */
    selectMobileSectionId(
        sectionId,
    ) {
        if (sectionId === this.selectedSectionId.mobile) {
            return;
        }

        // Find the nav section associated with the currently
        // selected section ID
        if (this.selectedSectionId.mobile !== null) {
            // A selection currently exists
            const navSection = this.navSections.find((navSection) => {
                return (
                    navSection.sectionId ===
                    this.selectedSectionId.mobile
                );
            });

            if (navSection) {
                // We found the nav section associated with the current
                // selection - hide it
                navSection.setMobileVisibility(false);
            }
        }

        // Find the nav section associated with the newly
        // selected section ID
        if (sectionId !== null) {
            // A new selection was specified
            const navSection = this.navSections.find((navSection) => {
                return (
                    navSection.sectionId ===
                    sectionId
                );
            });

            if (navSection) {
                // We found the nav section associated with the new
                // selection - show it
                navSection.setMobileVisibility(true);
            }
        }

        this.selectedSectionId.mobile = sectionId;

        // Adjust whether scrolling in the nav is enabled or not
        adjustScrolling.call(this);
    }

    /**
     * Shows the nav section associated with the specified ID, on desktop
     * @param {string} sectionId
     */
    selectDesktopSectionId(
        sectionId,
    ) {
        if (sectionId === this.selectedSectionId.desktop) {
            return;
        }

        // Find the nav section associated with the currently
        // selected section ID
        if (this.selectedSectionId.desktop !== null) {
            // A selection currently exists
            const navSection = this.navSections.find((navSection) => {
                return (
                    navSection.sectionId ===
                    this.selectedSectionId.desktop
                );
            });

            if (navSection) {
                // We found the nav section associated with the current
                // selection - hide it
                navSection.setDesktopVisibility(false);
            }
        }

        // Find the nav section associated with the newly
        // selected section ID
        if (sectionId !== null) {
            // A new selection was specified
            const navSection = this.navSections.find((navSection) => {
                return (
                    navSection.sectionId ===
                    sectionId
                );
            });

            if (navSection) {
                // We found the nav section associated with the new
                // selection - show it
                navSection.setDesktopVisibility(true);
            }
        }

        this.selectedSectionId.desktop = sectionId;
    }

    /**
     * Sets the show/hide visibility state of the header
     * while sticky
     * @param {boolean} visible
     */
    setVisibility(visible) {
        if (visible) {
            if (this.visible) {
                return;
            }

            // Become visible
            this.visible = true;
            this.scrollVelocity = 0;
            this.container.classList.remove(settings.classNames.hidden);
        } else {
            if (!this.visible) {
                return;
            }

            // Become hidden
            this.visible = false;
            this.scrollVelocity = 0;
            this.container.classList.add(settings.classNames.hidden);

            // Close and reset section selector
            closeAndResetSectionSelector.call(this);

            // If a submenu is open, close it
            if (this.openSubmenu) {
                this.openSubmenu.close();
            }

            // If something has focus in the header, blur it
            if (document.activeElement) {
                if (this.container.contains(document.activeElement)) {
                    document.activeElement.blur();
                }
            }
        }
    }

    openMenu(immediate = false) {
        if (this.menuOpen) {
            return;
        }

        this.menuOpen = true;

        // Stop any ongoing animation
        if (this.menuTimeline) {
            this.menuTimeline.progress(1);
        }

        // Lock scrolling
        disableBodyScroll(this.nav);

        // Create backdrop
        this.backdrop = document.createElement('div');
        this.backdrop.className = settings.classNames.backdrop;

        const backdropClickCallback = (event) => {
            // Stop if we're closed or closing
            if (!this.menuOpen) {
                return;
            }

            // Close the menu
            toggleMobileMenu.call(this, event);
        };

        this.backdrop.addEventListener('click', backdropClickCallback);
        this.backdrop.addEventListener('touchstart', backdropClickCallback);

        this.container.parentNode.insertBefore(this.backdrop, this.container);

        // Add our open classname
        this.container.classList.add(settings.classNames.open);

        // Show the nav menu and remove any styles
        this.nav.removeAttribute('style');
        this.navMenuContainer.removeAttribute('style');
        this.nav.style.display = 'block';

        if (immediate) {
            // Show our backdrop
            this.backdrop.style.opacity = 1;

            // Adjust whether scrolling in the nav is enabled or not
            adjustScrolling.call(this);
            return;
        }

        // Hide overflow & block pointer events during the transition
        this.navMenuContainer.style.overflow = 'hidden';
        this.navMenuContainer.style.pointerEvents = 'none';

        // Create timeline
        this.menuTimeline = gsap.timeline();

        this.menuTimeline.to(
            this.backdrop,
            0.5,
            {
                ease: 'power2.out',
                opacity: 1,
            },
            0,
        );

        this.menuTimeline.from(
            this.navMenuContainer,
            0.5,
            {
                ease: 'power2.out',
                height: 0,
            },
            0,
        );

        this.menuTimeline.add(function() {
            // Remove explicit height from the nav menu
            this.navMenuContainer.removeAttribute('style');

            // Adjust whether scrolling in the nav is enabled or not
            adjustScrolling.call(this);

            // Clear our timeline variable
            this.menuTimeline = null;
        }.bind(this));
    }

    closeMenu(immediate = false) {
        if (!this.menuOpen) {
            return;
        }

        this.menuOpen = false;

        // Stop any ongoing animation
        if (this.menuTimeline) {
            this.menuTimeline.progress(1);
        }

        // Unlock scrolling
        enableBodyScroll(this.nav);

        // Remove our open & scroll classnames
        this.container.classList.remove(settings.classNames.open);
        this.container.classList.remove(settings.classNames.scroll);

        const finishCallback = () => {
            // Remove our backdrop
            this.backdrop.parentNode.removeChild(this.backdrop);
            this.backdrop = null;

            // Reset nav styles
            this.nav.removeAttribute('style');
            this.navMenuContainer.removeAttribute('style');

            // Close and reset section selector
            closeAndResetSectionSelector.call(this, true);
        };

        if (immediate) {
            finishCallback();

            // Stop here
            return;
        }

        // Hide overflow & block pointer events during the transition
        this.navMenuContainer.style.overflow = 'hidden';
        this.navMenuContainer.style.pointerEvents = 'none';

        // Create timeline
        this.menuTimeline = gsap.timeline();

        this.menuTimeline.to(
            this.backdrop,
            0.5,
            {
                ease: 'power2.out',
                opacity: 0,
            },
            0,
        );

        this.nav.style.height = '';
        this.menuTimeline.to(
            this.navMenuContainer,
            0.5,
            {
                ease: 'power2.out',
                height: 0,
            },
            0,
        );

        this.menuTimeline.add(function() {
            finishCallback();

            // Clear our timeline variable
            this.menuTimeline = null;
        }.bind(this));
    }
}


/*
    Internal functions
*/

/**
 * Toggles opening & closing the mobile menu
 * @this Header
 * @param {event} event
 */
function toggleMobileMenu(event) {
    event.preventDefault();
    event.stopPropagation();

    // Stop if we're not in mobile mode
    if (!WindowMetrics.isMobile()) {
        return;
    }

    // Toggle nav menu visibility
    if (this.menuOpen) {
        this.closeMenu();
    } else {
        this.openMenu();
    }
}

/**
 * Toggles opening & closing the mobile menu
 * @this Header
 */
function adjustScrolling() {
    // Check if the height of the nav content can fit onscreen
    if (this.nav.clientHeight < this.navMenuContainer.clientHeight - 1) {
        // Enable scrolling
        this.container.classList.add(settings.classNames.scroll);
    } else {
        // Disable scrolling
        this.container.classList.remove(settings.classNames.scroll);
    }
}

/**
 * Closes the section selector
 * @this Header
 * @param {boolean?} immediate
 */
function closeAndResetSectionSelector(immediate = false) {
    // If the section selector is open, close it
    if (this.sectionSelector.isOpen) {
        this.sectionSelector.close(immediate);
    }

    // Reset the section selection to its initial state
    this.sectionSelector.resetSectionId();
}
